import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { PublicCreativeUnitPackageListing } from '../../../../../../../api/src/creative-unit-package-listing/creative-unit-package-listing.entity';
import { CartService } from '../../cart/cart.service';
import { CartUtils, LocaleScope } from '../../cart/cart.utils';
import { CartItemGroup } from '../../cart/cart.model';
import { SelectedPartialListingEvent } from '../../../../pages/project/project.page';

@Component({
	selector: 'app-creative-unit-package-listing-list',
	templateUrl: './creative-unit-package-listing-list.component.html',
	styleUrls: ['./creative-unit-package-listing-list.component.scss']
})
export class CreativeUnitPackageListingListComponent implements OnChanges {
	@Input() listings: PublicCreativeUnitPackageListing[];
	@Input() layout = 'grid-4';
	@Input() thumbnailStyle = 'contain';
	@Input() searchQuery: string = '';
	@Input() showSizeDetails: boolean = false;

	@Output() selectedListing: EventEmitter<PublicCreativeUnitPackageListing> = new EventEmitter<PublicCreativeUnitPackageListing>();
	@Output() selectedPartialListing: EventEmitter<SelectedPartialListingEvent> = new EventEmitter<SelectedPartialListingEvent>();

	public listingDetailMap: { [listingId: string]: CartItemGroup[] } = {};
	public listingChildDetailMap: { [childListingId: string]: CartItemGroup[] } = {};

	public listingShowChildrenMap: { [listingId: string]: 'top-level' | 'deep' | null } = {};

	constructor(
		public cartService: CartService
	) {}

	ngOnChanges(changes: SimpleChanges) {
		if ((changes.listings?.currentValue?.length || changes.showSizeDetails?.currentValue) && this.listings?.length) {
			if (this.showSizeDetails) {
				this.createListingDetailMap(this.listings);

			} else {
				this.listingDetailMap = null;
				this.listingChildDetailMap = null;
				this.listingShowChildrenMap = null;
			}
		}
	}

	private processListingDetails(listing: PublicCreativeUnitPackageListing): CartItemGroup[] {
		if (!listing?.creativeUnitPackageSummary?.creativeUnits?.length) {
			return null;
		}

		// add listing name to each unit so we can convert it to a group
		const units = [...listing.creativeUnitPackageSummary.creativeUnits].map(cu => {
			return {
				...cu,
				metadata: {
					...cu.metadata,
					listingName: listing.name,
					listingId: listing.id
				}
			};
		});

		let groups = this.cartService.convertItemsToGroups(units, 'both');

		if (this.searchQuery) {
			groups = CartUtils.filterGroupsByQuery(groups, this.searchQuery?.toLowerCase(), LocaleScope.EVERY, true);

			if (groups.length) {
				return groups;
			} else return null;
		}

		return groups;
	}

	/**
	 * Only return hits that are not at the top level, meaning ones with subgroups or items.
	 * @param groups
	 * @private
	 */
	private filterOutTopLevelHits(groups: CartItemGroup[]) {
		const filtered = groups?.filter(g => g.subgroups?.length || g.items?.length);

		if (filtered?.length) {
			return filtered;
		} else return null;
	}

	/**
	 * Create a map of listing details for each listing, including children if applicable.
	 * Children hits are separated between 'deep' (subgroups or items) and 'top-level' (only the surface listing details).
	 * @param listings
	 * @private
	 */
	private createListingDetailMap(listings: PublicCreativeUnitPackageListing[]) {
		this.listingDetailMap = {};
		this.listingChildDetailMap = {};
		this.listingShowChildrenMap = {};

		listings.forEach(listing => {
			const parentGroups = this.processListingDetails(listing);
			const deepParentGroups = this.filterOutTopLevelHits(parentGroups);
			this.listingDetailMap[listing.id] = deepParentGroups;

			// if there are no deep hits for the parent, check if the hit was within the children
			if (!deepParentGroups && listing.children?.length) {
				let topLevelMatches: number = 0;

				// iterate through children
				listing.children.forEach(child => {
					const childGroups = this.processListingDetails(child);
					const deepChildGroups = this.filterOutTopLevelHits(childGroups);
					this.listingChildDetailMap[child.id] = deepChildGroups;

					if (deepChildGroups) {
						this.listingShowChildrenMap[listing.id] = 'deep';
					} else if (childGroups && this.listingShowChildrenMap?.[listing.id] !== 'deep') {
						topLevelMatches += 1;
						this.listingShowChildrenMap[listing.id] = 'top-level';
					}
				});

				// if we are getting top-level matches for all children,
				// then prevent breaking the preview into children and just show the parent
				if (listing.childrenIds?.length === topLevelMatches) {
					this.listingShowChildrenMap[listing.id] = null;
				}
			}
		});
	}
}
